// frontend/src/pages/admin/ManageUsersPage.tsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Users, PlusCircle, MoreHorizontal, Search, Edit3, Trash2, ShieldCheck, UserCog, Loader2, AlertCircle, FileWarning, LayoutDashboard } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { getUsers, updateUser, deleteUserByAdmin } from '@/api/userService';
import { type UserAdminView, type UserRole, type UserStatus } from '@/types';

const ManageUsersPage = () => {
  const [users, setUsers] = useState<UserAdminView[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<number | null>(null); // For edit/delete operations
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchAdminUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetchedUsers = await getUsers({ limit: 100 }); // Add pagination later if needed
      
      let usersToDisplay = fetchedUsers;
      if (searchTerm) {
        usersToDisplay = fetchedUsers.filter(user =>
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          user.role.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      setUsers(usersToDisplay);

    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setError("Unauthorized. Please log in again.");
        localStorage.removeItem('authToken');
        navigate('/login?sessionExpired=true');
      } else {
        setError("Failed to fetch users. Please try again later.");
      }
      console.error("Error fetching admin users:", err);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
        fetchAdminUsers();
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, navigate]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadgeVariant = (status: UserStatus): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'active': return 'default';
      case 'suspended': return 'destructive';
      case 'pending_verification': return 'secondary';
      default: return 'outline';
    }
  };

  const getRoleBadgeVariant = (role: UserRole): "default" | "secondary" => {
    return role === 'admin' ? 'default' : 'secondary';
  };

  const handleRoleChange = async (userId: number, newRole: UserRole) => {
    if (!window.confirm(`Are you sure you want to change user ID ${userId}'s role to ${newRole}?`)) return;
    setIsProcessing(userId);
    setError(null);
    try {
      await updateUser(userId, { role: newRole });
      setUsers(prevUsers => prevUsers.map(user =>
        user.id === userId ? { ...user, role: newRole } : user
      ));
      // alert(`User role updated to ${newRole}.`); // Use toast
    } catch (err: any) {
      console.error(`Failed to update role for user ${userId}:`, err);
      const errorMsg = err.response?.data?.detail || "Failed to update user role.";
      setError(errorMsg);
      // alert(errorMsg); // Use toast
    } finally {
      setIsProcessing(null);
    }
  };
  
  const handleStatusChange = async (userId: number, newStatus: UserStatus) => {
    if (!window.confirm(`Are you sure you want to change user ID ${userId}'s status to ${newStatus}?`)) return;
    setIsProcessing(userId);
    setError(null);
    try {
      await updateUser(userId, { status: newStatus });
      setUsers(prevUsers => prevUsers.map(user =>
        user.id === userId ? { ...user, status: newStatus } : user
      ));
      // alert(`User status updated to ${newStatus}.`); // Use toast
    } catch (err: any) {
      console.error(`Failed to update status for user ${userId}:`, err);
      const errorMsg = err.response?.data?.detail || "Failed to update user status.";
      setError(errorMsg);
      // alert(errorMsg); // Use toast
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const userToDelete = users.find(u => u.id === userId);
    if (userToDelete?.role === 'admin') {
        // Ideally, the backend also prevents self-deletion or deletion of other admins by non-superadmins
        alert("Admin users cannot be deleted through this interface for safety. Consider demoting first or using direct database actions if necessary.");
        return;
    }
    if (!window.confirm(`Are you sure you want to delete user ${userToDelete?.email || userId}? This action cannot be undone.`)) return;
    
    setIsProcessing(userId);
    setError(null);
    try {
      await deleteUserByAdmin(userId);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      // alert("User deleted successfully."); // Use toast
    } catch (err: any) {
      console.error(`Failed to delete user ${userId}:`, err);
      const errorMsg = err.response?.data?.detail || "Failed to delete user.";
      setError(errorMsg);
      // alert(errorMsg); // Use toast
    } finally {
      setIsProcessing(null);
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading users...</p>
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Users</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={fetchAdminUsers} variant="outline">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <Users className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Manage <span className="text-gradient-maroon-to-gold">Users</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              View, edit roles, and manage user accounts.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mt-4 sm:mt-0">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
              <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Link>
            </Button>
            <Button asChild className="!bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
              <Link to="/admin/users/new">
                <PlusCircle className="mr-2 h-5 w-5" /> Add New User
              </Link>
            </Button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Input
            type="search"
            placeholder="Search users by name, email, role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-border focus:border-primary focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
        </div>
      </div>

      {error && !isLoading && users.length > 0 && (
         <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
           <AlertCircle className="h-5 w-5 mr-2" /> {error}
         </div>
      )}

      <Card className="shadow-lg bg-card border border-border/30 pt-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader className='bg-vedasMaroon-800'>
              <TableRow className="border-border/50">
                <TableHead className="w-[25%] text-primary-foreground py-4">Name</TableHead>
                <TableHead className="w-[30%] text-primary-foreground">Email</TableHead>
                <TableHead className="text-primary-foreground">Role</TableHead>
                <TableHead className="text-primary-foreground">Status</TableHead>
                <TableHead className="text-primary-foreground">Registered</TableHead>
                <TableHead className="text-right text-primary-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                     <div className="flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                        Loading users...
                    </div>
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id} className="border-border/40 hover:bg-muted/30 dark:hover:bg-muted/10">
                    <TableCell className="font-medium text-foreground">
                        {user.name || <span className="italic text-muted-foreground/80">No name</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)} className="capitalize">
                        {user.role === 'admin' ? <ShieldCheck className="mr-1.5 h-3.5 w-3.5 opacity-80" /> : <UserCog className="mr-1.5 h-3.5 w-3.5 opacity-80" />}
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(user.status)} className="capitalize">
                        {user.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(user.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger className='justify-self-end' asChild>
                          {isProcessing === user.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover text-popover-foreground border-border">
                          <DropdownMenuLabel>User: {user.email}</DropdownMenuLabel>
                          <DropdownMenuItem asChild className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                            <Link to={`/admin/users/edit/${user.id}`}>
                              <Edit3 className="mr-2 h-4 w-4 text-primary/80" /> Edit User
                            </Link>
                          </DropdownMenuItem>

                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                                <UserCog className="mr-2 h-4 w-4 text-primary/80" /> Change Role
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="bg-popover text-popover-foreground border-border">
                                <DropdownMenuRadioGroup value={user.role} onValueChange={(newRole) => handleRoleChange(user.id, newRole as UserRole)}>
                                    <DropdownMenuRadioItem value="user" className="focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">User</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="admin" className="focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">Admin</DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          
                          <DropdownMenuSub>
                             <DropdownMenuSubTrigger className="hover:bg-muted/50 dark:hover:bg-muted/20 focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">
                                <UserCog className="mr-2 h-4 w-4 text-primary/80" /> Change Status
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="bg-popover text-popover-foreground border-border">
                                <DropdownMenuRadioGroup value={user.status} onValueChange={(newStatus) => handleStatusChange(user.id, newStatus as UserStatus)}>
                                    <DropdownMenuRadioItem value="active" className="focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">Active</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="suspended" className="focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">Suspended</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="pending_verification" className="focus:bg-muted/50 dark:focus:bg-muted/20 cursor-pointer">Pending Verification</DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>

                          <DropdownMenuSeparator className="bg-border/50"/>
                          <DropdownMenuItem
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-destructive focus:text-destructive-foreground focus:bg-destructive/90 cursor-pointer"
                            disabled={isProcessing === user.id || user.email === 'admin@example.com'} // Example: Prevent deleting a core admin
                          >
                            {isProcessing === user.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                             Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    No users found.
                    {searchTerm && " Try adjusting your search or clearing it."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageUsersPage;